import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldAlert, CircleCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckinScanPage() {
  const { token } = useParams();
  const { t, lang } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const [data, setData] = useState(null);
  const [invalid, setInvalid] = useState(false);

  const isAdmin = !!(user && user.role === "admin");

  useEffect(() => {
    if (!isAdmin) return;
    axios
      .get(`${API}/admin/checkin/${token}`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setInvalid(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, token]);

  if (user === null) {
    return <div className="min-h-screen bg-ink" data-testid="scan-loading" />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="scan-admin-only">
        <div className="border border-line bg-surface p-10 max-w-md text-center">
          <ShieldAlert size={36} className="text-blaze mx-auto mb-5" />
          <h1 className="font-display text-3xl font-extrabold uppercase text-bone">{t.scan.adminOnly}</h1>
          <button
            data-testid="scan-login-btn"
            onClick={() => setAuthOpen(true)}
            className="mt-7 font-telemetry text-xs uppercase tracking-[0.2em] bg-blaze text-ink px-7 py-3.5 hover:bg-sand transition-colors"
          >
            {t.scan.loginAdmin}
          </button>
        </div>
      </div>
    );
  }

  const confirm = async (eventId) => {
    try {
      await axios.post(`${API}/admin/checkin-scan`, { token, event_id: eventId }, { withCredentials: true });
      setData((d) => ({
        ...d,
        bookings: d.bookings.map((b) => (b.id === eventId ? { ...b, checkin_at: new Date().toISOString() } : b)),
      }));
      toast.success(t.scan.done);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.auth.genericError);
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="checkin-scan-page">
      <header className="border-b border-line bg-surface/60">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <img src="/logo.png" alt="Dust Republic" className="h-10 w-auto" />
          <Link
            to="/"
            data-testid="scan-back-home-link"
            className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.2em] text-sandmut hover:text-blaze transition-colors"
          >
            <ArrowLeft size={14} />
            {t.scan.back}
          </Link>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-12">
        <p className="font-telemetry text-xs uppercase tracking-[0.25em] text-blaze mb-3">{t.scan.title}</p>

        {invalid && (
          <div className="border border-[#D94235]/50 bg-[#D94235]/10 p-8 text-center" data-testid="scan-invalid">
            <p className="font-display text-2xl font-extrabold uppercase text-bone">{t.scan.invalid}</p>
          </div>
        )}

        {data && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="border border-line bg-surface p-6 mb-8 flex items-center gap-5" data-testid="scan-member-card">
              <div className="w-14 h-14 bg-blaze/15 border border-blaze/50 flex items-center justify-center shrink-0">
                <span className="font-display font-black text-2xl uppercase text-blaze">
                  {data.member.name?.charAt(0) || <UserRound size={22} />}
                </span>
              </div>
              <div>
                <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-bone leading-tight">
                  {data.member.name}
                </h1>
                <p className="font-telemetry text-[11px] text-ochre mt-1">
                  {data.member.email} — {data.member.vehicle || "—"}
                  {data.member.rig && (
                    <span className="text-sandmut">
                      {" "}· {data.member.rig.tires?.toUpperCase()}{data.member.rig.winch ? " + WINCH" : ""}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <h2 className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-sandmut mb-4">
              {t.scan.bookings}
            </h2>
            {data.bookings.length === 0 && (
              <p className="font-telemetry text-xs uppercase tracking-[0.2em] text-sandmut py-10 text-center">
                {t.scan.noBookings}
              </p>
            )}
            <div className="flex flex-col gap-3" data-testid="scan-bookings-list">
              {data.bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid={`scan-booking-${b.id}`}
                  className="border border-line bg-surface/60 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
                >
                  <div>
                    <p className="font-display text-lg font-bold uppercase text-bone">{b.title[lang]}</p>
                    <p className="font-telemetry text-[10px] uppercase tracking-[0.15em] text-sandmut">
                      {b.date} — {b.location}
                    </p>
                  </div>
                  {b.checkin_at ? (
                    <span className="flex items-center gap-2 font-telemetry text-[11px] uppercase tracking-[0.15em] text-[#7FB383]">
                      <CircleCheck size={15} />
                      {t.scan.confirmed}
                    </span>
                  ) : (
                    <button
                      data-testid={`scan-confirm-${b.id}`}
                      onClick={() => confirm(b.id)}
                      className="font-telemetry text-[11px] uppercase tracking-[0.2em] bg-blaze text-ink px-5 py-3 hover:bg-sand transition-colors"
                    >
                      {t.scan.confirm}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
